import React, { useState, useCallback } from 'react';
import GridLayout from 'react-grid-layout';
import { ActiveJobCard, DonutChartWidget, ListWidget, ProgressBarWidget } from './widgets';
import './Dashboard.css';

// Default layout configuration
const defaultLayout = [
  { i: 'activeJob1', x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  { i: 'activeJob2', x: 3, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  { i: 'activeJob3', x: 6, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
  { i: 'donutSalesManager', x: 0, y: 2, w: 4, h: 3, minW: 3, minH: 3 },
  { i: 'listEndClient', x: 4, y: 2, w: 5, h: 3, minW: 3, minH: 3 },
  { i: 'progressSalesManager', x: 0, y: 5, w: 4, h: 3, minW: 3, minH: 3 },
  { i: 'donutCandidate', x: 4, y: 5, w: 4, h: 3, minW: 3, minH: 3 },
];

// Sample data - replace with API data later
const sampleData = {
  activeJobs: [
    { id: 1, status: 'Active', title: 'Senior Developer', description: 'Looking for experienced React developers with 5+ years experience' },
    { id: 2, status: 'Urgent', title: 'Project Manager', description: 'Need PM for large enterprise project starting next month' },
    { id: 3, status: 'Pending', title: 'Data Analyst', description: 'Entry level data analyst position for healthcare client' },
  ],
  jobBySalesManager: [
    { name: 'Active', value: 47, color: '#3B82F6' },
    { name: 'Pending', value: 30, color: '#E5E7EB' },
    { name: 'Closed', value: 23, color: '#10B981' },
  ],
  jobByEndClient: [
    { id: 1, name: 'Acme Corp', subtitle: 'Technology', value: 15, subValue: 'jobs' },
    { id: 2, name: 'TechStart Inc', subtitle: 'Startup', value: 12, subValue: 'jobs' },
    { id: 3, name: 'Global Finance', subtitle: 'Finance', value: 10, subValue: 'jobs' },
    { id: 4, name: 'Health Systems', subtitle: 'Healthcare', value: 8, subValue: 'jobs' },
  ],
  jobBySalesManagerProgress: [
    { id: 1, name: 'John Smith', value: 85, color: '#3B82F6', subtitle: '17 of 20 jobs filled' },
    { id: 2, name: 'Sarah Johnson', value: 72, color: '#10B981', subtitle: '18 of 25 jobs filled' },
    { id: 3, name: 'Mike Davis', value: 60, color: '#F59E0B', subtitle: '12 of 20 jobs filled' },
    { id: 4, name: 'Emily Brown', value: 45, color: '#EF4444', subtitle: '9 of 20 jobs filled' },
  ],
  jobByCandidate: [
    { name: 'Placed', value: 50, color: '#10B981' },
    { name: 'In Progress', value: 30, color: '#3B82F6' },
    { name: 'Available', value: 20, color: '#E5E7EB' },
  ],
};

const DashboardGrid = ({ onWidgetClick, width = 1200, savedLayout, onLayoutSave }) => {
  const [layout, setLayout] = useState(savedLayout || defaultLayout);

  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    localStorage.setItem('dashboardLayout', JSON.stringify(newLayout));
    if (onLayoutSave) {
      onLayoutSave(newLayout);
    }
  }, [onLayoutSave]);

  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case 'activeJob1':
        return <ActiveJobCard data={sampleData.activeJobs[0]} onClick={onWidgetClick} />;
      case 'activeJob2':
        return <ActiveJobCard data={sampleData.activeJobs[1]} onClick={onWidgetClick} />;
      case 'activeJob3':
        return <ActiveJobCard data={sampleData.activeJobs[2]} onClick={onWidgetClick} />;
      case 'donutSalesManager':
        return (
          <DonutChartWidget
            title="Job by Sales Manager"
            data={sampleData.jobBySalesManager}
            centerValue="47%"
            centerLabel="Active"
            onClick={onWidgetClick}
          />
        );
      case 'listEndClient':
        return (
          <ListWidget
            title="Job by Sales End Client"
            data={sampleData.jobByEndClient}
            onClick={onWidgetClick}
          />
        );
      case 'progressSalesManager':
        return (
          <ProgressBarWidget
            title="Job by Sales Manager"
            data={sampleData.jobBySalesManagerProgress}
            onClick={onWidgetClick}
          />
        );
      case 'donutCandidate':
        return (
          <DonutChartWidget
            title="Job by Candidate"
            data={sampleData.jobByCandidate}
            centerValue="50%"
            centerLabel="Placed"
            onClick={onWidgetClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <GridLayout
      className="layout"
      layout={layout}
      cols={12}
      rowHeight={80}
      width={width}
      onLayoutChange={handleLayoutChange}
      draggableHandle=".drag-handle"
      isDraggable={true}
      isResizable={true}
      compactType="vertical"
      preventCollision={false}
    >
      {layout.map((item) => (
        <div key={item.i} className="relative group">
          <div className="drag-handle absolute top-0 left-0 right-0 h-8 cursor-move z-10 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex items-center justify-center h-full">
              <div className="flex gap-1">
                <span className="w-1 h-1 bg-gray-400 rounded-full" />
                <span className="w-1 h-1 bg-gray-400 rounded-full" />
                <span className="w-1 h-1 bg-gray-400 rounded-full" />
              </div>
            </div>
          </div>
          <div className="h-full">{renderWidget(item.i)}</div>
        </div>
      ))}
    </GridLayout>
  );
};

export default DashboardGrid;
